/**
 * Whitelist of search parameters that are shared between home and drilldown
 * routes. These parameters should be preserved when navigating back from a
 * drilldown route to the home route.
 */
const SHARED_SEARCH_PARAMS = [
  'tab',
  'view',
  'pId', // Provider IDs
  'atId', // Appointment Type IDs
  'aId', // Address IDs
  'bcId', // Booking Channel IDs
  'bat', // Base Appointment Types
  'astId', // Assessment Type IDs
  'fp', // Forms Priority
  'dp', // Date preset
  'from', // Date range start
  'to', // Date range end
] as const;

/**
 * Extracts shared search parameters from current URL search params that should
 * be preserved when navigating back to the home route from a drilldown route.
 */
export function extractSharedSearchParams(searchParams: URLSearchParams): URLSearchParams {
  const result = new URLSearchParams();

  for (const param of SHARED_SEARCH_PARAMS) {
    const values = searchParams.getAll(param);
    for (const value of values) {
      result.append(param, value);
    }
  }

  return result;
}

/**
 * Search params to compare when determining if the drilldown should show the
 * disclaimer dialog. The list is explicit because some of these might be set
 * before opening a drilldown to persist them between tabs, yet they aren't
 * used in the drilldown.
 */
const SEARCH_PARAMS_TO_COMPARE_BY_TYPE = {
  appointments: ['dp', 'from', 'to', 'pId', 'aId', 'bcId', 'bat', 'atId'],
  forms: ['dp', 'from', 'to', 'pId', 'astId', 'fp'],
} as const;

/**
 * Determines if there are changes between two sets of filters search params
 * for a specific drilldown.
 */
export function hasFilterChangesByType(args: {
  initialSearchParams: URLSearchParams;
  activeSearchParams: URLSearchParams;
  type: 'appointments' | 'forms';
}) {
  const areArraysEqual = (arrayA: string[], arrayB: string[]) => {
    if (arrayA.length !== arrayB.length) return false;
    const sorted1 = [...arrayA].sort();
    const sorted2 = [...arrayB].sort();
    return sorted1.every((val, index) => val === sorted2[index]);
  };

  return SEARCH_PARAMS_TO_COMPARE_BY_TYPE[args.type].some(
    (key) =>
      !areArraysEqual(args.initialSearchParams.getAll(key), args.activeSearchParams.getAll(key))
  );
}
